import { ChevronLeft } from 'lucide-react';
import {
  type AnnouncementCategory,
  type AnnouncementTargetApp,
} from '@/services/api';
import { sanitizeAnnouncementHtml } from '@/utils/sanitizeHtml';

export interface PreviewData {
  targetApp: AnnouncementTargetApp;
  category: AnnouncementCategory;
  title: string;
  contentHtml: string;
  isImportant: boolean;
  isPinned: boolean;
  displayStartAt?: string;
  displayEndAt?: string;
}

const CATEGORY_LABEL: Record<AnnouncementCategory, string> = {
  SYSTEM: '업데이트',
  MAINTENANCE: '점검',
  EVENT: '이벤트',
  GENERAL: '공지',
};

const CATEGORY_TONE: Record<
  AnnouncementCategory,
  { bg: string; text: string }
> = {
  SYSTEM: { bg: 'bg-blue-100', text: 'text-blue-700' },
  MAINTENANCE: { bg: 'bg-amber-100', text: 'text-amber-700' },
  EVENT: { bg: 'bg-green-100', text: 'text-green-700' },
  GENERAL: { bg: 'bg-gray-100', text: 'text-gray-700' },
};

const TARGET_LABEL: Record<AnnouncementTargetApp, string> = {
  BUSINESS: '비즈니스앱',
  GUARDIAN: '보호자앱',
};

function todayStr(): string {
  const d = new Date();
  const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function nowDateTimeStr(): string {
  const d = new Date();
  const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  );
}

/** 게시 기간 상태 (start/end는 "YYYY-MM-DD HH:mm:ss" 또는 "YYYY-MM-DD") */
export function getDisplayState(start?: string, end?: string) {
  const now = nowDateTimeStr();
  if (start && start > now) return { label: '게시 예정', tone: 'text-amber-600' };
  if (end && end < now) return { label: '게시 종료', tone: 'text-gray-500' };
  return { label: '노출 중', tone: 'text-green-600' };
}

interface Props {
  data: PreviewData;
  /** 모바일 프레임 외곽(베젤/그림자) 표시 여부 */
  framed?: boolean;
}

/**
 * 보호자/비즈니스앱 공지사항 상세 화면을 모바일 사이즈로 표시.
 * - 폼 옆 sticky 패널, 모달 양쪽에서 동일하게 재사용
 */
export function AnnouncementMobilePreview({ data, framed = true }: Props) {
  const safeHtml = sanitizeAnnouncementHtml(data.contentHtml);
  const tone = CATEGORY_TONE[data.category];
  const previewDate = todayStr().replace(/-/g, '.') + ' (작성 미리보기)';

  return (
    <div
      className={
        framed
          ? 'mx-auto w-full max-w-[375px] overflow-hidden rounded-[28px] border-[6px] border-black bg-[#F5F5F5] shadow-md'
          : 'mx-auto w-full max-w-[375px] overflow-hidden rounded-2xl border border-gray-200 bg-[#F5F5F5]'
      }
    >
      {/* 상태바 모사 */}
      <div className="flex items-center justify-between bg-white px-4 py-1 text-[11px] text-gray-700">
        <span>9:41</span>
        <span className="flex items-center gap-1">
          <span>●●●●●</span>
          <span>100%</span>
        </span>
      </div>

      {/* LayoutHeader 모사 */}
      <div className="flex items-center border-b border-[#EEEEEE] bg-white px-2 py-3">
        <button type="button" aria-label="뒤로" className="p-1 text-gray-700">
          <ChevronLeft size={20} />
        </button>
        <div className="flex-1 text-center text-[15px] font-semibold text-[#222222]">
          공지사항
        </div>
        <div className="w-7" />
      </div>

      {/* 본문 */}
      <div className="px-3 pb-6 pt-4">
        <div className="rounded-xl border-2 border-[#E9E9E9] bg-white p-4">
          {/* 배지 */}
          <div className="mb-2 flex flex-wrap items-center gap-1.5">
            {data.isPinned && (
              <span className="inline-flex items-center rounded bg-orange-100 px-1.5 py-0.5 text-[11px] font-semibold text-orange-600">
                고정
              </span>
            )}
            {data.isImportant && (
              <span className="inline-flex items-center rounded bg-red-100 px-1.5 py-0.5 text-[11px] font-semibold text-red-600">
                중요
              </span>
            )}
            <span
              className={`inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-semibold ${tone.bg} ${tone.text}`}
            >
              {CATEGORY_LABEL[data.category]}
            </span>
            <span className="ml-auto inline-flex items-center rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">
              {TARGET_LABEL[data.targetApp]}
            </span>
          </div>

          {/* 제목 */}
          <h2
            className={`mb-1 text-[17px] leading-snug ${
              data.isImportant ? 'font-bold text-[#222222]' : 'font-bold'
            }`}
          >
            {data.title.trim() || (
              <span className="text-gray-400">제목 미입력</span>
            )}
          </h2>
          <p className="mb-4 text-xs text-[#999999]">{previewDate}</p>

          {/* 본문 */}
          {safeHtml ? (
            <div
              className="announcement-rich-content"
              dangerouslySetInnerHTML={{ __html: safeHtml }}
            />
          ) : (
            <div className="rounded-md border border-dashed border-gray-200 p-4 text-center text-xs text-gray-400">
              내용을 입력하면 여기에 표시됩니다.
            </div>
          )}

          {/* 게시 기간 안내 (실제 앱에는 없지만 작성자에게 도움) */}
          {(data.displayStartAt || data.displayEndAt) && (
            <div className="mt-4 rounded-md bg-gray-50 px-3 py-2 text-[11px] text-gray-500">
              게시 기간:{' '}
              {data.displayStartAt
                ? data.displayStartAt.slice(0, 16).replace('T', ' ')
                : '제한 없음'}{' '}
              ~{' '}
              {data.displayEndAt
                ? data.displayEndAt.slice(0, 16).replace('T', ' ')
                : '제한 없음'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
